'use client';

import { useId, useLayoutEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { RailButton } from './GlobalNavigationRail';
import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';
import styles from './AccountNavigation.module.css';

const ITEM_SELECTOR = 'button:not(:disabled), a[href], summary';

/** Keep secondary destinations discoverable without filling the navigation rail. */
export function NavigationRailMenu({ label, icon, active, highlight, onOpen, footer, children }: {
  label: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  active?: boolean;
  highlight?: boolean;
  onOpen?: () => void;
  footer?: (close: () => void) => ReactNode;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const button = anchor.current?.getBoundingClientRect();
      const menu = panel.current?.getBoundingClientRect();
      if (!button || !menu) return;
      setPosition({
        left: Math.max(12, Math.min(button.right + 12, window.innerWidth - menu.width - 12)),
        top: Math.max(12, Math.min(button.top, window.innerHeight - menu.height - 12)),
      });
    };
    place();
    const observer = new ResizeObserver(place);
    if (panel.current) observer.observe(panel.current);
    const dismiss = (event: PointerEvent) => {
      if (!anchor.current?.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) close();
    };
    const onScroll = (event: Event) => {
      if (!panel.current?.contains(event.target as Node)) place();
    };
    document.addEventListener('pointerdown', dismiss);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      observer.disconnect();
      document.removeEventListener('pointerdown', dismiss);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const positioned = position !== null;
  useLayoutEffect(() => {
    if (open && positioned) panel.current?.querySelector<HTMLElement>(ITEM_SELECTOR)?.focus();
  }, [open, positioned]);

  return <div ref={anchor}>
    <RailButton icon={icon} label={label} active={active || open} highlight={highlight} aria-haspopup="dialog" aria-expanded={open}
      aria-controls={open ? id : undefined} onClick={() => { setPosition(null); if (!open) onOpen?.(); setOpen(value => !value); }} />
    {open && createPortal(
      <div ref={panel} id={id} role="dialog" aria-label={label}
        style={{ left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? 'visible' : 'hidden' }}
        className={`${styles.popover} fixed z-[230] flex max-h-[calc(100dvh-24px)] w-80 flex-col max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-[#DCE4EC] bg-white p-3 text-[#263445] shadow-[0_16px_48px_rgba(15,23,42,0.16)]`}
        onKeyDown={event => {
          if (event.defaultPrevented) return;
          if (event.key === 'Escape' || event.key === 'Tab') {
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); }
            close();
            anchor.current?.querySelector('button')?.focus();
          } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const items = Array.from(panel.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
            const index = items.indexOf(document.activeElement as HTMLElement);
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
              : (index + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
            items[next]?.focus();
          }
        }}>
        <LiquidGlass className={styles.surfaceLens}><span /></LiquidGlass>
        <p className="mb-2 px-2 text-xs font-semibold text-[#7F8899]">{label}</p>
        <div className="min-h-0 overflow-y-auto overscroll-contain">{children(close)}</div>
        {footer && <div className="shrink-0 pt-2">{footer(close)}</div>}
      </div>, document.body,
    )}
  </div>;
}

export const NAVIGATION_MENU_ITEM_CLASS_NAME = 'flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-[13px] font-medium text-[#596879] transition-colors hover:bg-[#F0F4F7] hover:text-[#263445] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]';

